/**
 * Screen-space clustering for the Guide's maps (pure). Pins that would sit
 * on top of each other at the current zoom are gathered into one cluster
 * marker with a count; clicking it zooms to its members. The maps project
 * their pins to pixels, pass them here, and place the clusters it returns.
 */

export type ScreenPoint = { key: string; x: number; y: number };

export type Cluster = {
  keys: string[];
  /** The centroid, in the same pixel space as the input. */
  x: number;
  y: number;
};

/** Pins closer than this many pixels gather into one cluster. */
export const CLUSTER_RADIUS_PX = 44;
/** From this zoom on, every pin stands alone (venues on one street stay apart). */
export const CLUSTER_MAX_ZOOM = 15.5;

/**
 * Groups points within `radius` of each other, greedily from the top-left,
 * so the result is stable for the same input. Singles come back as
 * one-key clusters; callers show those as ordinary pins.
 */
export function clusterPoints(points: ScreenPoint[], radius = CLUSTER_RADIUS_PX): Cluster[] {
  const sorted = [...points].sort((a, b) => a.y - b.y || a.x - b.x || a.key.localeCompare(b.key));
  const taken = new Set<string>();
  const out: Cluster[] = [];
  const r2 = radius * radius;
  for (const seed of sorted) {
    if (taken.has(seed.key)) continue;
    taken.add(seed.key);
    const members = [seed];
    let cx = seed.x;
    let cy = seed.y;
    for (const p of sorted) {
      if (taken.has(p.key)) continue;
      const dx = p.x - cx;
      const dy = p.y - cy;
      if (dx * dx + dy * dy <= r2) {
        taken.add(p.key);
        members.push(p);
        // The cluster's centre follows its members.
        cx = members.reduce((sum, m) => sum + m.x, 0) / members.length;
        cy = members.reduce((sum, m) => sum + m.y, 0) / members.length;
      }
    }
    out.push({ keys: members.map((m) => m.key), x: cx, y: cy });
  }
  return out;
}

/** The pixel box around a set of points, padded, for zooming into a cluster. */
export function clusterBounds(points: ScreenPoint[], pad = 0): { minX: number; minY: number; maxX: number; maxY: number } | null {
  if (points.length === 0) return null;
  const xs = points.map((p) => p.x);
  const ys = points.map((p) => p.y);
  return {
    minX: Math.min(...xs) - pad,
    minY: Math.min(...ys) - pad,
    maxX: Math.max(...xs) + pad,
    maxY: Math.max(...ys) + pad,
  };
}

/** Great-circle distance in kilometres between two points. */
export function distanceKm(a: { latitude: number; longitude: number }, b: { latitude: number; longitude: number }): number {
  const toRad = (deg: number) => (deg * Math.PI) / 180;
  const dLat = toRad(b.latitude - a.latitude);
  const dLng = toRad(b.longitude - a.longitude);
  const s =
    Math.sin(dLat / 2) ** 2 + Math.cos(toRad(a.latitude)) * Math.cos(toRad(b.latitude)) * Math.sin(dLng / 2) ** 2;
  return 2 * 6371 * Math.asin(Math.sqrt(s));
}

/** "850 m", "2.3 km", "18 km": a distance a visitor reads at a glance. */
export function formatDistance(km: number, lang: "en" | "ar"): string {
  const n = (value: number, digits: number) => new Intl.NumberFormat(lang === "ar" ? "ar-EG" : "en-GB", { maximumFractionDigits: digits }).format(value);
  if (km < 1) return lang === "ar" ? `${n(Math.round(km * 100) * 10, 0)} م` : `${n(Math.round(km * 100) * 10, 0)} m`;
  if (km < 10) return lang === "ar" ? `${n(km, 1)} كم` : `${n(km, 1)} km`;
  return lang === "ar" ? `${n(Math.round(km), 0)} كم` : `${n(Math.round(km), 0)} km`;
}
